import * as React from "react";
import {
    QueryTableRowProps,
    QueryTableHeaderProps,
    QueryTableSortHeaderProps,
    QueryTablePaginationProps,
    QueryTableColumnProps,
    QueryTableProps
} from "./QueryTable.interfaces";
import { QueryFilterIcon } from "../QueryFilterIcon";
import { toKebabCase } from "../../../misc/Helpers/StringHelper";

const QueryTableRow = (props: QueryTableRowProps) => {
    const firstChild = React.Children.toArray(props.children)[0] as React.ReactElement<
        QueryTableColumnProps
        >;

    if (firstChild && firstChild.props.overrideRowMap) {
        const row = firstChild.props.overrideRowMap(props.row);

        if (row) {
            return row;
        }
    }

    const columns = React.Children.map(
        props.children,
        (child: React.ReactElement<QueryTableColumnProps>, _index) => {
            return <td data-automationid={`column${_index}`}>{child.props.map(props.row)}</td>;
        }
    );

    return <tr style={{ height: "50px", wordWrap: "break-word" }} data-automationid={`row${props.index}`}>{columns}</tr>;
};

const QueryTableHeader = (props: QueryTableHeaderProps) => {
    const { header, member, sort, filterable, filtered, onSort, onFilter } = props;

    return (
        <th data-automationid={`header${props.index}`}>
            {header}
            <QueryTableSortHeader 
                member={member}
                sort={sort} 
                onSort={onSort}
                automationid={`${toKebabCase(header.split(' ').join(''))}_Sort`}
            />
            <QueryFilterIcon
                member={member}
                filterable={filterable}
                filtered={filtered}
                onFilter={onFilter}
                automationid={`${toKebabCase(header.split(' ').join(''))}_Filter`}
            />
        </th>
    );
};

const QueryTableSortHeader = (props: QueryTableSortHeaderProps) => {
    const { member, sort, onSort } = props;
    const handleSort = (event: any) => onSort(member, event);

    if (sort === "Ascending") {
        return (
            <a onClick={handleSort} data-automationid={props.automationid}>
                <i
                    style={{ paddingLeft: "8px" }}
                    className="fa fa-sort-up text-info"
                    aria-hidden="true"
                />
            </a>
        );
    }
    if (sort === "Descending") {
        return (
            <a onClick={handleSort} data-automationid={props.automationid}>
                <i
                    style={{ paddingLeft: "8px" }}
                    className="fa fa-sort-down text-info"
                    aria-hidden="true"
                />
            </a>
        );
    }
    if (sort) {
        return (
            <a onClick={handleSort} data-automationid={props.automationid}>
                <i style={{ paddingLeft: "8px" }} className="fa fa-sort" aria-hidden="true" />
            </a>
        );
    }

    return null;
};

const QueryTablePagination = (props: QueryTablePaginationProps) => {
    const { currentPage, lastPage, onPage } = props;
    const firstPage = 1;
    const jump = 2;
    let previousPage = currentPage - 1;
    let nextPage = currentPage + 1;
    let start = currentPage - jump;
    let end = currentPage + jump;
    const pages = [];

    if (start < firstPage) {
        end += start * -1 + 1;
    }
    if (end > lastPage) {
        start -= end - lastPage;
    }
    if (start < firstPage) {
        start = firstPage;
    }
    if (end > lastPage) {
        end = lastPage;
    }
    if (previousPage < firstPage) {
        previousPage = firstPage;
    }
    if (nextPage > lastPage) {
        nextPage = lastPage;
    }

    for (let page = start; page <= end; page++) {
        pages.push(
            <li key={page} className={page === currentPage ? "active" : null}>
                <a onClick={event => onPage(page, event)}>
                    {page === start && page !== firstPage
                        ? "..."
                        : page === end && page !== lastPage
                            ? "..."
                            : page}
                </a>
            </li>
        );
    }

    return (
        <nav aria-label="Table pages">
            <ul className="pagination">
                <li className={currentPage === firstPage ? "disabled" : null}>
                    <a onClick={event => onPage(firstPage, event)} aria-label="Last">
                        <span aria-hidden="true">
                            <i className="fa fa-angle-double-left" aria-hidden="true" />
                        </span>
                    </a>
                </li>
                <li className={currentPage === firstPage ? "disabled" : null}>
                    <a onClick={event => onPage(previousPage, event)} aria-label="Previous">
                        <span aria-hidden="true">
                            <i className="fa fa-angle-left" aria-hidden="true" />
                        </span>
                    </a>
                </li>
                {pages}
                <li className={currentPage === lastPage ? "disabled" : null}>
                    <a onClick={event => onPage(nextPage, event)} aria-label="Next">
                        <span aria-hidden="true">
                            <i className="fa fa-angle-right" aria-hidden="true" />
                        </span>
                    </a>
                </li>
                <li className={currentPage === lastPage ? "disabled" : null}>
                    <a onClick={event => onPage(lastPage, event)} aria-label="Last">
                        <span aria-hidden="true">
                            <i className="fa fa-angle-double-right" aria-hidden="true" />
                        </span>
                    </a>
                </li>
            </ul>
        </nav>
    );
};

export class QueryTableColumn<T = any> extends React.Component<QueryTableColumnProps<T>> {
    render() {
        return this.props.isHidden === true
            ? null
            : <td>{this.props.children}</td>;
    }
}

export class QueryTable extends React.Component<QueryTableProps> {
    render() {
        const { onPage, onSort, onFilter, query, rowUniqueIdentifier, isLoading, disablePaging } = this.props;
        let children = this.props.children;

        if (Array.isArray(children)) {
            children = children.filter(x => x !== null && x.props.isHidden !== true);
        }

        const { Order, Page, Filters, Data } = query;

        const columnsLen = children.length;
        let rows;

        if (isLoading || !Data) {
            rows = (
                <tr style={{ height: "50px", whiteSpace: "nowrap" }}>
                    <td className="text-center" colSpan={columnsLen}>
                        Loading data...
                    </td>
                </tr>
            );
        } else if (!Array.isArray(Data)) {
            throw new Error("Invalid data supplied to query table.");
        } else if (Data.length < 1) {
            rows = (
                <tr style={{ height: "50px", whiteSpace: "nowrap" }}>
                    <td className="text-center" colSpan={columnsLen}>
                        No data available
                    </td>
                </tr>
            );
        } else {
            rows = Data.map((row, index) => {
                return (
                    <QueryTableRow key={rowUniqueIdentifier(row)} row={row} index={index}>
                        {children}
                    </QueryTableRow>
                );
            });
        }

        const currentPage = (Page && Page.Current) || 1;
        const lastPage = (Page && Page.Total) || 1;

        const headers = React.Children.map(
            children,
            (child: React.ReactElement<QueryTableColumnProps>, index) => {

                if (child.props.isHidden === true) {
                    return null;
                }

                const { member, header, sortable, filterable } = child.props;
                const sort = sortable && Order && Order.Member == member ? Order.Direction : sortable;
                const filtered = Filters && Filters.some(filter => filter.Member === member);

                return (
                    <QueryTableHeader
                        key={index}
                        index={index}
                        member={member}
                        header={header}
                        sort={sort}
                        filterable={filterable}
                        filtered={filtered}
                        onSort={onSort}
                        onFilter={onFilter}
                    />
                );
            }
        );

        return (
            <div id={this.props.id} data-automationid="tablearea" style={{overflow: "auto"}}>
                <table className="table table-striped table-bordered table-hover">
                    <thead>
                        <tr style={{ height: "50px", whiteSpace: "nowrap" }}>{headers}</tr>
                    </thead>
                    <tbody data-automationid={`${isLoading || !Data ? 'loading' : 'loaded'}`}>{rows}</tbody>
                </table>
                <div>
                    {!disablePaging ? <QueryTablePagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        onPage={onPage}
                    /> : null}
                </div>
            </div>
        );
    }
}
